import { ValuesOf } from 'types/utils'

type DefaultChoices<T> = {
  [K in Language]: Array<{
    value: T
    label: string
  }>
}

export const PHONE = 'phone'
export const NOT_CONTACTED_REASON = 'not_contacted_reason'
export const REGISTERED = 'registered'
export const FEEDBACK = 'feedback'
export const ADDRESS = 'address'
export const DUPLICATE = 'duplicate'
export const DOB = 'dob'
export const NEEDS_ATTENTION_OTHER = 'needs_attention_other'

export const ENGLISH = 'en'
export const SPANISH = 'es'

export type Language = typeof ENGLISH | typeof SPANISH

export const questionKeys = [
  REGISTERED,
  PHONE,
  DOB,
  ADDRESS,
  FEEDBACK,
  NOT_CONTACTED_REASON,
  DUPLICATE,
  NEEDS_ATTENTION_OTHER,
] as const

export type QuestionKey = ValuesOf<typeof questionKeys>

export const requiredQuestionKeys = [
  REGISTERED,
  DOB,
  ADDRESS,
  FEEDBACK,
] as const

export type RequiredQuestionKey = ValuesOf<typeof requiredQuestionKeys>

const BOOLEAN_RESPONSES = {
  YES: 'yes',
  NO: 'no',
  NO_RESPONSE: 'no_response',
} as const

export type BooleanResponse = ValuesOf<typeof BOOLEAN_RESPONSES>

const FEEDBACK_RESPONSES = {
  POSITIVE_FEEDBACK: 'positive_feedback',
  NEGATIVE_FEEDBACK: 'negative_feedback',
  NO_FEEDBACK: 'no_feedback',
} as const

export type FeedbackResponse = ValuesOf<typeof FEEDBACK_RESPONSES>

const NOT_CONTACTED_RESPONSES = {
  NOT_HOME: 'not_home',
  LANGUAGE_BARRIER: 'language_barrier',
  REFUSED: 'refused',
  CALL_BACK: 'call_back',
  BUSY: 'busy',
  DISCONNECTED: 'disconnected',
  DO_NOT_CALL: 'do_not_call',
  WRONG_PHONE: 'wrong_phone',
  VOICEMAIL_NOT_APPLICANT: 'voicemail_not_applicant',
  VOICEMAIL_MATCHES_APPLICANT: 'voicemail_matches_applicant',
  NO_PHONE: 'no_phone',
} as const

export type NotContactedResponse = ValuesOf<typeof NOT_CONTACTED_RESPONSES>

export function isNotContactedResponse(
  response: PhoneVerificationResponseKey
): response is NotContactedResponse {
  return Object.values<string>(NOT_CONTACTED_RESPONSES).includes(response)
}

export const responseKeys = {
  ...BOOLEAN_RESPONSES,
  ...FEEDBACK_RESPONSES,
  ...NOT_CONTACTED_RESPONSES,
} as const

export type PhoneVerificationResponseKey =
  | NotContactedResponse
  | BooleanResponse
  | FeedbackResponse

export const notContactedChoices = {
  [ENGLISH]: [
    { value: responseKeys.NOT_HOME, label: 'Not Home' },
    {
      value: responseKeys.LANGUAGE_BARRIER,
      label: 'Language barrier',
    },
    { value: responseKeys.REFUSED, label: 'Refused' },
    { value: responseKeys.CALL_BACK, label: 'Call back' },
    { value: responseKeys.BUSY, label: 'Busy' },
    { value: responseKeys.DISCONNECTED, label: 'Disconnected' },
    { value: responseKeys.DO_NOT_CALL, label: 'Do Not Call' },
    { value: responseKeys.WRONG_PHONE, label: 'Wrong Number' },
    {
      value: responseKeys.VOICEMAIL_NOT_APPLICANT,
      label: 'Voicemail Not Applicant',
    },
    {
      value: responseKeys.VOICEMAIL_MATCHES_APPLICANT,
      label: 'Voicemail Matches Applicant',
    },
    { value: responseKeys.NO_PHONE, label: 'No phone number' },
  ],
  [SPANISH]: [
    { value: responseKeys.NOT_HOME, label: 'No hogar' },
    {
      value: responseKeys.LANGUAGE_BARRIER,
      label: 'Barrera del idioma',
    },
    { value: responseKeys.REFUSED, label: 'Rechazado' },
    { value: responseKeys.CALL_BACK, label: 'Llamar de vuelta' },
    { value: responseKeys.BUSY, label: 'Ocupado' },
    { value: responseKeys.DISCONNECTED, label: 'Desconectado' },
    { value: responseKeys.DO_NOT_CALL, label: 'No llames' },
    { value: responseKeys.WRONG_PHONE, label: 'Número equivocado' },
    {
      value: responseKeys.VOICEMAIL_NOT_APPLICANT,
      label: 'Correo de voz no solicitante',
    },
    {
      value: responseKeys.VOICEMAIL_MATCHES_APPLICANT,
      label: 'Correo de voz si es del solicitante',
    },
    {
      value: responseKeys.NO_PHONE,
      label: 'Sin número de teléfono',
    },
  ],
} as const satisfies DefaultChoices<NotContactedResponse>

export type NotContactedChoice =
  (typeof notContactedChoices)[Language][number]['value']

export const RESPONSES_REQUIRING_NOTES = [
  [FEEDBACK, [responseKeys.POSITIVE_FEEDBACK, responseKeys.NEGATIVE_FEEDBACK]],
  [NEEDS_ATTENTION_OTHER, [responseKeys.YES]],
  [REGISTERED, [responseKeys.NO]],
] as const

export const excludeFromQueue: PhoneVerificationResponseKey[] = [
  responseKeys.NO_PHONE,
  responseKeys.WRONG_PHONE,
  responseKeys.DO_NOT_CALL,
  responseKeys.REFUSED,
  responseKeys.DISCONNECTED,
  responseKeys.LANGUAGE_BARRIER,
]

type InterpolationValues = {
  [DOB]: { dob: string }
  [REGISTERED]: { registrationDate: string }
  [ADDRESS]: { address: string }
}

const questionText: Record<
  Language,
  {
    [T in QuestionKey]?: T extends keyof InterpolationValues
      ? (interpValue: InterpolationValues[T]) => string
      : () => string
  }
> = {
  [ENGLISH]: {
    [DOB]: ({ dob }) =>
      `We have your DOB as ${dob || '[dob]'}. Is that correct?`,

    [REGISTERED]: ({ registrationDate }) =>
      `Did you register to vote on ${registrationDate || '[date]'}?`,

    [ADDRESS]: ({ address }) =>
      `The address I have in your application is ${
        address || '[address]'
      }. Is that correct?`,

    [FEEDBACK]: () =>
      `And finally, was the person who helped you register friendly and courteous?`,

    [NOT_CONTACTED_REASON]: () => `Call result if not contacted`,

    [DUPLICATE]: () => `Form may be a duplicate`,
    [NEEDS_ATTENTION_OTHER]: () => `Needs attention for some other reason`,
  },
  [SPANISH]: {
    [DOB]: ({ dob }: { dob: string }) =>
      `La fecha de nacimiento que tenemos es ${dob || '[dob]'}. Esta correcta?`,

    [REGISTERED]: ({ registrationDate }: { registrationDate: string }) =>
      `Te registraste para votar el ${registrationDate || '[date]'}?`,

    [ADDRESS]: ({ address }: { address: string }) =>
      `La direccion que tengo en la aplicacion es ${
        address || '[address]'
      }. Esta correcta?`,

    [FEEDBACK]: () => `Y cómo le trató la persona que le ayudó a registrarse?`,

    [NOT_CONTACTED_REASON]: () => `Resultado de la llamada si no se contacta`,

    [DUPLICATE]: () => `El formulario puede ser un duplicado`,
    [NEEDS_ATTENTION_OTHER]: () => `Necesita atención por otro motivo`,
  },
}

const booleanChoices = {
  [ENGLISH]: [
    { value: responseKeys.YES, label: 'verified' },
    { value: responseKeys.NO, label: 'needs attention' },
    { value: responseKeys.NO_RESPONSE, label: 'refused to answer' },
  ],
  [SPANISH]: [
    { value: responseKeys.YES, label: 'verificado' },
    { value: responseKeys.NO, label: 'necesita atención' },
    {
      value: responseKeys.NO_RESPONSE,
      label: 'se negó a responder',
    },
  ],
} as const satisfies DefaultChoices<BooleanResponse>

const feedbackChoices = {
  [ENGLISH]: [
    { value: 'positive_feedback', label: 'positive feedback' },
    { value: 'negative_feedback', label: 'negative feedback' },
    { value: 'no_feedback', label: 'no feedback' },
  ],
  [SPANISH]: [
    { value: 'positive_feedback', label: 'comentario positivo' },
    { value: 'negative_feedback', label: 'comentario negativo' },
    { value: 'no_feedback', label: 'sin comentario' },
  ],
} as const satisfies DefaultChoices<FeedbackResponse>

const choices = (key: QuestionKey, language: Language) => {
  switch (key) {
    case NOT_CONTACTED_REASON:
      return notContactedChoices[language]
    case FEEDBACK:
      return feedbackChoices[language]
    default:
      return booleanChoices[language]
  }
}

export type ScriptQuestion = {
  id?: number
  key: QuestionKey
  content: (typeof questionText)[Language][QuestionKey]
  choices: ReturnType<typeof choices>
  response: string
}

export type QuestionsRecord = Record<QuestionKey, ScriptQuestion>
const makeScriptQuestions = (language: Language) =>
  questionKeys.reduce(
    (questions, key) => ({
      ...questions,
      [key]: {
        key,
        content: questionText[language][key],
        choices: choices(key, language),
        response: '',
      },
    }),
    {} as QuestionsRecord
  )

export const phoneVerificationQuestions: Record<
  Language,
  Record<QuestionKey, ScriptQuestion>
> = {
  [ENGLISH]: makeScriptQuestions(ENGLISH),
  [SPANISH]: makeScriptQuestions(SPANISH),
}
